import { useEffect, useState } from 'react';

interface CompatRouter {
  push: (url: string, options?: { scroll?: boolean }) => void;
  refresh: () => void;
  pathname: string;
  searchParams: URLSearchParams | null;
}

/**
 * Compatibility hook that provides a unified router interface
 * Works with both Pages Router and App Router
 */
export function useCompatRouter(): CompatRouter {
  const [pathname, setPathname] = useState<string>('');
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(
    null
  );
  const [router, setRouter] = useState<any>(null);
  const [isAppRouter, setIsAppRouter] = useState<boolean | null>(null);

  useEffect(() => {
    // Detect which router is available
    const detectRouter = async () => {
      try {
        // Try App Router first
        const appNav = await import('next/navigation');
        // Check if the hooks exist (they are functions)
        if (
          typeof appNav.useRouter === 'function' &&
          typeof appNav.usePathname === 'function' &&
          typeof appNav.useSearchParams === 'function'
        ) {
          setIsAppRouter(true);
          return;
        }
      } catch {
        // App Router not available
      }

      try {
        // Fall back to Pages Router
        const pagesRouter = await import('next/router');
        if (typeof pagesRouter.useRouter === 'function') {
          setIsAppRouter(false);
          return;
        }
      } catch {
        // Pages Router not available
      }

      console.error('No Next.js router detected');
    };

    detectRouter();
  }, []);

  useEffect(() => {
    if (isAppRouter === null) return;

    if (isAppRouter) {
      // Use App Router
      import('next/navigation').then(nav => {
        const routerInstance = nav.useRouter();
        const pathValue = nav.usePathname();
        const searchValue = nav.useSearchParams();

        setRouter(routerInstance);
        setPathname(pathValue);
        setSearchParams(searchValue);
      });
    } else {
      // Use Pages Router
      import('next/router').then(routerModule => {
        const routerInstance = routerModule.useRouter();

        setRouter(routerInstance);
        setPathname(routerInstance.pathname);

        // Convert query object to URLSearchParams
        const params = new URLSearchParams();
        Object.entries(routerInstance.query).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
          } else if (value) {
            params.append(key, value);
          }
        });
        setSearchParams(params);
      });
    }
  }, [isAppRouter]);

  const push = (url: string, options?: { scroll?: boolean }) => {
    if (!router) return;

    if (isAppRouter) {
      // App Router push
      router.push(url, options);
    } else {
      // Pages Router push
      router.push(url, undefined, { scroll: options?.scroll ?? true });
    }
  };

  const refresh = () => {
    if (!router) return;

    if (isAppRouter) {
      // App Router refresh
      router.refresh();
    } else {
      // Pages Router - reload current page
      router.replace(router.asPath);
    }
  };

  return {
    push,
    refresh,
    pathname,
    searchParams,
  };
}
